import { Button, Grid, HStack, Icon, Square, Stack, Text, VStack } from '@chakra-ui/react';
import Dropzone, { IInputProps, ILayoutProps } from 'react-dropzone-uploader';
import { useTranslation } from 'react-i18next';
import { AiOutlineFileText } from 'react-icons/ai';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { IoTrash } from 'react-icons/io5';

import { fontWeights } from '../global/Fonts';

const Preview = ({ meta: { name } }: { meta: { [name: string]: any } }) => {
  return (
    <Stack direction={'row'} color="blue.500" spacing={2} alignItems={'center'}>
      <Icon as={AiOutlineFileText} boxSize="5" />
      <Text as="span" fontSize="md" fontWeight={fontWeights.bold}>
        {name}
      </Text>
    </Stack>
  );
};

const Input = ({ accept, onFiles, getFilesFromEvent, extra, className, disabled }: IInputProps) => {
  const { t } = useTranslation();
  const { active } = extra;
  return (
    <Grid placeContent={'center'} gap="3" w={'full'} h={'full'} position={'relative'}>
      <Square size="10" bg={active ? 'blue.500' : 'bg-subtle'} borderRadius="lg" m={'auto'}>
        <Icon as={active ? FiChevronDown : FiChevronUp} boxSize="5" color={active ? 'white' : 'muted'} />
      </Square>
      <VStack spacing="1">
        <HStack spacing="1" whiteSpace="nowrap">
          <Button variant="link" color="blue.500" disabled={disabled} size="sm" position={'static'}>
            {/* We need the label component for the file upload to be triggered by the package*/}
            <label style={{ cursor: 'pointer' }}>
              {active ? t('components:dropzone.drop') : t('components:dropzone.click_to_upload')}
              <input
                style={{
                  opacity: 0,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 1,
                  cursor: 'pointer',
                }}
                className={className}
                type="file"
                disabled={disabled}
                accept={accept}
                onChange={async (e) => {
                  const chosenFiles = await getFilesFromEvent(e);
                  onFiles(chosenFiles as File[]);
                }}
                value=""
              />
            </label>
          </Button>
          <Text fontSize="sm" color="muted" display={active ? 'none' : 'block'}>
            {t('components:dropzone.drag_drop')}
          </Text>
        </HStack>
      </VStack>
    </Grid>
  );
};

const Layout = ({ input, previews, dropzoneProps, files, extra: { maxFiles, active, reject } }: ILayoutProps) => {
  const { t } = useTranslation();
  let borderColor = 'gray.200';
  let borderStyle = 'solid';

  if (reject) {
    borderColor = 'red.200';
  }

  if (active) {
    borderColor = 'blue.500';
    borderStyle = 'dashed';
  }

  const hasFiles = files.length > 0;

  if (hasFiles && !active) {
    borderColor = 'blue.500';
  }
  return (
    <Grid
      borderWidth={1}
      borderStyle={borderStyle}
      borderRadius="lg"
      borderColor={borderColor}
      h={40}
      bg={active ? 'blue.100' : 'white'}
      transition={'all .2s ease-in'}
    >
      <div {...dropzoneProps}>{files.length < maxFiles && input}</div>
      {files.length > 0 && (
        <Stack alignItems={'center'} spacing={4}>
          {previews}
          <Button
            size={'sm'}
            variant={'link'}
            colorScheme={'red'}
            onClick={() => files[0].remove()}
            aria-label={'remove file'}
            leftIcon={<Icon as={IoTrash} boxSize="5" />}
          >
            {t('common:remove')}
          </Button>
        </Stack>
      )}
    </Grid>
  );
};

type Props = {
  onChange: (arg0: any) => void;
  maxFiles?: number;
  identifier: string;
  disabled: boolean;
};

export const DropzoneInput = ({ onChange, maxFiles, disabled = false, identifier }: Props) => {
  const handleChangeStatus = (file: object, status: string) => {
    switch (status) {
      case 'done':
        onChange(file);
        break;
      case 'removed':
        onChange(undefined);
    }
  };
  return (
    <Dropzone
      LayoutComponent={Layout}
      PreviewComponent={Preview}
      maxFiles={maxFiles ?? 1}
      multiple={false}
      onChangeStatus={handleChangeStatus}
      InputComponent={Input}
      submitButtonDisabled
      disabled={disabled}
      accept="image/png,image/jpg,image/jpeg,application/pdf"
      maxSizeBytes={1024 * 1024 * 16} //1024 * 1024 is 1MB
      addClassNames={{ input: identifier }}
    />
  );
};
